import { useEffect, useState } from 'react'
import AppSidebar from '@/components/AppSidebar'
import { apiService } from '@/service/api'
import { useAppStore } from '@/store'
import { useTranslation } from '@/i18n'
import {
  User, Users, Sparkles, BarChart3, HelpCircle, FileText,
  Settings as SettingsIcon, Activity, Bot, Shield, ShieldCheck,
  Palette, Network, RefreshCw,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

// Import all settings sections
import ProfilePhotoField from '@/components/ProfilePhotoField'
import TeamV2 from '@/routes/Settings/Team/TeamV2'
import AgentsV2 from '@/routes/Settings/AgentsV2'
import AppearanceSettings from '@/routes/Settings/AppearanceSettings'
import PlatformAgentProvisioning from '@/routes/Settings/PlatformAgentProvisioning'
import OnboardingQuestions from '@/routes/Settings/OnboardingQuestions'
import AuditTrailV2 from '@/routes/Settings/AuditLog/AuditTrailV2'
import KnowledgeGraph from '@/routes/Settings/KnowledgeGraph'
import PipelineReprocessing from '@/routes/Settings/PipelineReprocessing'
import EgressPolicyControl from '@/routes/SettingsPage/EgressPolicyControl'
import UserInteractionData from '@/components/UserInteractionData'
import HelpAgentLauncherSlot from '@/components/HelpAgent/HelpAgentLauncherSlot'
import { PAGE_HEADER, PAGE_TITLE } from '@/components/layout/pageHeader'

type Section =
  | 'profile' | 'team' | 'agents' | 'agent-nicknames' | 'appearance'
  | 'features' | 'security' | 'usage' | 'onboarding' | 'logs' | 'advanced' | 'queues'
  | 'platform-agents' | 'knowledge-graph' | 'pipeline-reprocessing'

interface SidebarItem {
  id: Section
  label: string
  icon: LucideIcon
  adminOnly?: boolean
  internalOnly?: boolean
  comingSoon?: boolean
}

export default function SettingsV2() {
  const { t } = useTranslation()
  const { user } = useAppStore()
  const isAdmin = user?.role === 'admin'
  const isInternalUser = (user?.email?.endsWith('@intuigence.ai') || user?.email?.endsWith('@intuigence.com')) ?? false
  const [activeSection, setActiveSection] = useState<Section>('profile')

  // Needs-attention chip on the Knowledge Graph item — mirrors the legacy
  // SettingsPage. Only the internal admins who can see the KG section fetch
  // the count; failures stay silent so Settings always mounts.
  const canSeeKg = isAdmin && isInternalUser
  const [kgNeedsAttention, setKgNeedsAttention] = useState<number | null>(null)
  useEffect(() => {
    if (!canSeeKg) return
    let cancelled = false
    apiService
      .fetchOrError(apiService.getUrl('v1/knowledge-graph/uploads/counts'))
      .then((r) => r.json())
      .then((data: { needs_attention?: number }) => {
        if (!cancelled && typeof data?.needs_attention === 'number') {
          setKgNeedsAttention(data.needs_attention)
        }
      })
      .catch(() => {
        // Silent — Settings must mount regardless of KG counts availability.
      })
    return () => {
      cancelled = true
    }
  }, [canSeeKg])

  const sidebarItems: SidebarItem[] = [
    { id: 'profile', label: t('common:settings.tabs.profile'), icon: User },
    { id: 'team', label: t('common:settings.tabs.team'), icon: Users },
    { id: 'agents', label: t('common:settings.tabs.agents'), icon: Bot },
    { id: 'appearance', label: t('common:settings.tabs.appearance'), icon: Palette, adminOnly: true },
    { id: 'features', label: t('common:settings.tabs.features'), icon: Sparkles, adminOnly: true, internalOnly: true },
    // adminOnly WITHOUT internalOnly, unlike `features`/`queues`/`platform-agents`:
    // the audience is the CUSTOMER tenant's org-admin, which is exactly what the
    // server-side gate encodes (`role === 'admin'`, no internal-email condition).
    // `features` hosts internal dev tooling (flag toggles annotated with their
    // Intuigence owner), so a tenant policy control there would be invisible to
    // the admin it exists for.
    { id: 'security', label: t('common:settings.tabs.security', 'Security'), icon: ShieldCheck, adminOnly: true },
    { id: 'usage', label: t('common:settings.tabs.usage_limits'), icon: BarChart3, comingSoon: true },
    { id: 'onboarding', label: t('common:settings.tabs.onboarding_questions'), icon: HelpCircle, adminOnly: true, comingSoon: true },
    { id: 'logs', label: t('common:settings.tabs.audit_trail', 'Audit Trail'), icon: FileText, adminOnly: true },
    { id: 'queues', label: t('common:settings.tabs.queue_monitor'), icon: Activity, adminOnly: true, internalOnly: true },
    { id: 'platform-agents', label: t('common:settings.tabs.platform_agents'), icon: Shield, adminOnly: true, internalOnly: true },
    { id: 'knowledge-graph', label: t('common:settings.tabs.knowledge_graph', 'Knowledge Graph'), icon: Network, adminOnly: true, internalOnly: true },
    // FS-637 — separate page rather than a Knowledge Graph tab: it is about the
    // PIPELINE's effect across a tenant's drawings, not any single upload's state.
    // internalOnly for the same reason the KG page is — it exposes pipeline
    // internals and a GPU-spend control.
    { id: 'pipeline-reprocessing', label: t('common:settings.tabs.pipeline_reprocessing', 'Pipeline Reprocessing'), icon: RefreshCw, adminOnly: true, internalOnly: true },
    { id: 'advanced', label: t('common:settings.tabs.advanced'), icon: SettingsIcon, adminOnly: true, comingSoon: true },
  ]

  const visibleItems = sidebarItems.filter(
    (item) => (!item.adminOnly || isAdmin) && (!item.internalOnly || isInternalUser)
  )

  const renderContent = () => {
    switch (activeSection) {
      case 'profile':
        return (
          // No VISIBLE in-pane <h2>: the topbar already names the active
          // section, and repeating it read as a double heading. sr-only keeps
          // the section reachable via heading navigation (greptile on #30210).
          <div className="space-y-6">
            <h2 className="sr-only">{t('common:settings.tabs.profile')}</h2>
            <div className="flex flex-col gap-6 p-6 rounded-lg border border-[#e5e5e5] dark:border-dark-border">
              {/* FS-693: the avatar is now the upload control, so the name/email
                  sit beneath it rather than beside it — the control needs room
                  for its own buttons and hint line. */}
              <ProfilePhotoField />
              <div>
                <h4 className="text-base font-semibold">{user?.name}</h4>
                <span className="text-sm text-muted-foreground">{user?.email}</span>
              </div>
            </div>
            <div className="p-6 rounded-lg border border-[#e5e5e5] dark:border-dark-border">
              <label className="text-sm font-medium text-muted-foreground">{t('common:settings.organization_status')}</label>
              <div className="text-base mt-1">{t('common:settings.active')}</div>
            </div>
          </div>
        )
      case 'team': return <TeamV2 />
      case 'agents': return <AgentsV2 />
      case 'agent-nicknames': return <AgentsV2 />
      case 'appearance': return <AppearanceSettings />
      case 'features':
        return (
          <div className="space-y-6">
            <h2 className="sr-only">{t('common:settings.tabs.features')}</h2>
            <UserInteractionData />
          </div>
        )
      case 'security':
        return (
          <div className="space-y-6">
            <h2 className="sr-only">{t('common:settings.tabs.security', 'Security')}</h2>
            {/* Per-tenant egress-consent policy (Trust/Security §9 v1). The tab
                is adminOnly, matching the server-side `role === 'admin'` gate on
                PUT /v1/agent/egress-policy — the client gate is a convenience,
                the server one is the control. */}
            <EgressPolicyControl />
          </div>
        )
      case 'logs': return <AuditTrailV2 />
      case 'onboarding': return <OnboardingQuestions />
      case 'queues':
        return (
          <div
            className="bg-white dark:bg-dark-card rounded-lg border border-gray-200 dark:border-dark-border overflow-hidden"
            style={{ height: 'calc(100vh - 220px)' }}
          >
            <iframe
              src="/v2/api/admin/queues"
              className="w-full h-full border-0"
              title={t('common:settings.tabs.queue_monitor', 'Queue Monitor')}
            />
          </div>
        )
      case 'platform-agents': return <PlatformAgentProvisioning />
      case 'knowledge-graph': return <KnowledgeGraph />
      case 'pipeline-reprocessing': return <PipelineReprocessing />
      case 'usage': return <div className="text-muted-foreground">{t('common:general.coming_soon')}</div>
      case 'advanced': return <div className="text-muted-foreground">{t('common:general.coming_soon')}</div>
      default: return null
    }
  }

  return (
    <div className="flex h-screen">
      <AppSidebar />

      {/* Settings sub-nav */}
      <div className="flex w-[220px] shrink-0 flex-col border-e border-[#e5e5e5] bg-white dark:border-dark-border dark:bg-dark-card">
        <div className="px-4 py-6">
          <span className="text-sm font-semibold text-muted-foreground">{t('common:settings.sidebar_title')}</span>
        </div>
        <nav className="flex-1 overflow-y-auto px-3">
          {visibleItems.map((item) => {
            const Icon = item.icon
            const isActive = activeSection === item.id
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => !item.comingSoon && setActiveSection(item.id)}
                disabled={item.comingSoon}
                className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors mb-0.5 ${
                  isActive
                    ? 'bg-[#0e7490]/10 text-[#0e7490] font-medium'
                    : item.comingSoon
                      ? 'text-muted-foreground opacity-50 cursor-not-allowed'
                      : 'text-foreground hover:bg-muted'
                }`}
              >
                <Icon className="size-4 shrink-0" />
                <span className="flex-1 text-start">{item.label}</span>
                {/* Needs-attention chip — wired today only for the
                    knowledge-graph item. Hidden when count is 0 to avoid
                    visual noise on healthy tenants. */}
                {item.id === 'knowledge-graph' &&
                  kgNeedsAttention !== null &&
                  kgNeedsAttention > 0 && (
                    <span
                      className="ml-auto inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800 dark:bg-red-900/30 dark:text-red-300"
                      data-testid="kg-sidebar-chip"
                    >
                      {kgNeedsAttention}
                    </span>
                  )}
              </button>
            )
          })}
        </nav>
      </div>

      {/* Content */}
      <div className="flex flex-1 flex-col min-w-0">
        {/* Topbar */}
        <header className={`${PAGE_HEADER} justify-between`}>
          <span className={PAGE_TITLE}>
            {visibleItems.find((i) => i.id === activeSection)?.label || t('common:settings.sidebar_title')}
          </span>
          <div className="flex items-center gap-2">
            <HelpAgentLauncherSlot />
          </div>
        </header>

        {/* Main content */}
        <main className="flex-1 overflow-y-auto bg-white p-6 dark:bg-dark-card">
          {renderContent()}
        </main>
      </div>
    </div>
  )
}
